import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AuthForm from "@/components/auth/AuthForm";
import PawsBackground from "@/components/ui/PawsBackground";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { logger } from "@/utils/logger";
import { Analytics } from "@/services/analytics";
import { useTranslation } from "react-i18next";

const Auth: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("auth");

  useEffect(() => {
    Analytics.signupStarted();
  }, []);

  useEffect(() => {
    logger.debug("🔍 Page Auth", {
      loading,
      user: !!user,
      path: location.pathname,
    });

    // Si l'utilisateur est déjà authentifié ET email confirmé, le rediriger
    if (!loading && user && user.email_confirmed_at) {
      logger.debug("✅ Utilisateur déjà connecté, redirection depuis Auth...");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const from = (location.state as any)?.from?.pathname || "/";
      logger.debug("🎯 Redirection vers:", from);
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, location]);

  // Pendant le loading: même layout que le form pour éviter le décalage LCP
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505] p-4 relative">
        <PawsBackground />
        <div
          className="mb-8 w-full max-w-[220px] relative flex items-center justify-center"
          style={{ zIndex: 10 }}
        >
          <img
            src="/octoveto-logo.webp"
            alt="OctoVeto"
            className="w-full h-auto object-contain"
            fetchpriority="high"
            width="220"
            height="120"
          />
        </div>
        <div
          className="w-full max-w-[400px] flex justify-center"
          style={{ zIndex: 10 }}
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Si l'utilisateur est authentifié ET confirmé, ne pas afficher le formulaire
  if (user && user.email_confirmed_at) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505] p-4 relative">
      <PawsBackground />

      {/* Language switcher — top right */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      {/* Logo OctoVeto — Centré et Taille Pro */}
      <div
        className="mb-8 w-full max-w-[220px] relative flex items-center justify-center"
        style={{ zIndex: 10 }}
      >
        <img
          src="/octoveto-logo.webp"
          alt="OctoVeto"
          className="w-full h-auto object-contain"
          fetchpriority="high"
          width="220"
          height="120"
        />
      </div>

      <div className="w-full max-w-[400px] relative" style={{ zIndex: 10 }}>
        <AuthForm />
      </div>

      <footer className="mt-8 pb-4 text-center text-xs text-muted-foreground/80 space-x-3">
        <a href="/tos" className="hover:text-primary transition-colors">
          {t("footer_tos")}
        </a>
        <span>·</span>
        <a href="/privacy" className="hover:text-primary transition-colors">
          {t("footer_privacy")}
        </a>
        <span>·</span>
        <a
          href="mailto:contact@octoveto.com"
          className="hover:text-primary transition-colors"
        >
          {t("footer_contact")}
        </a>
      </footer>
    </div>
  );
};

export default Auth;
